"use client";

type CapacityPercentInput = {
    skillListeningSpeaking: number | null;
    skillLangNature: number | null;
    skillReading: number | null;
    skillWriting: number | null;
};

type CapacityRadarChartProps = {
    capacityPercent: CapacityPercentInput;
    className?: string;
    size?: number;
    variant?: "narrow" | "wide" | "popup";
};

const AXES = [
    {
        key: "skillListeningSpeaking" as const,
        label: "การฟัง การดู และการพูด",
        angleDeg: -90,
    },
    {
        key: "skillReading" as const,
        label: "การอ่าน",
        angleDeg: 0,
        nowrap: true,
    },
    {
        key: "skillLangNature" as const,
        label: "การเข้าใจธรรมชาติของภาษา และการใช้ภาษาไทย",
        angleDeg: 90,
    },
    {
        key: "skillWriting" as const,
        label: "การเขียน",
        angleDeg: 180,
        nowrap: true,
    },
];

function clampPercent(value: number | null) {
    if (value === null || !Number.isFinite(value)) {
        return 0;
    }
    return Math.max(0, Math.min(100, value));
}

function polarToCartesian(cx: number, cy: number, radius: number, angleDeg: number) {
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
        x: cx + radius * Math.cos(angleRad),
        y: cy + radius * Math.sin(angleRad),
    };
}

const CHART_RADIUS_RATIO = 0.42;
const LABEL_OFFSET_RATIO = 0.08;

/** ค่าเฉพาะ popup — ใหญ่พอดี modal แต่ยังมีที่ว่างสำหรับ label บน-ล่าง */
const POPUP_CHART_RADIUS_RATIO = 0.35;
const POPUP_LABEL_OFFSET_RATIO = 0.042;
const POPUP_VERTICAL_PADDING = "py-5";

export default function CapacityRadarChart({
    capacityPercent,
    className = "",
    size = 340,
    variant = "wide",
}: CapacityRadarChartProps) {
    const isPopup = variant === "popup";
    const cx = size / 2;
    const cy = size / 2;
    const maxRadius = size * (isPopup ? POPUP_CHART_RADIUS_RATIO : CHART_RADIUS_RATIO);
    const labelOffsetRatio = isPopup ? POPUP_LABEL_OFFSET_RATIO : LABEL_OFFSET_RATIO;
    const gridLevels = [0.25, 0.5, 0.75, 1];
    const labelClassName =
        variant === "narrow"
            ? "max-w-[108px] text-[11px] leading-snug"
            : "max-w-[130px] text-[12px] leading-snug";

    const dataPoints = AXES.map((axis) => {
        const value = clampPercent(capacityPercent[axis.key]);
        const point = polarToCartesian(cx, cy, (value / 100) * maxRadius, axis.angleDeg);
        return { ...axis, value, ...point };
    });

    const polygonPoints = dataPoints.map((p) => `${p.x},${p.y}`).join(" ");

    return (
        <div className={`relative mx-auto w-full ${isPopup ? `px-1 ${POPUP_VERTICAL_PADDING}` : ""} ${className}`}>
            <div className="relative aspect-square w-full">
                <svg
                    viewBox={`0 0 ${size} ${size}`}
                    className="h-full w-full"
                    role="img"
                    aria-label="กราฟสมรรถนะเฉลี่ยแต่ละด้าน"
                >
                {gridLevels.map((level) => (
                    <circle
                        key={level}
                        cx={cx}
                        cy={cy}
                        r={maxRadius * level}
                        fill="none"
                        stroke="#D1D5DB"
                        strokeWidth={1}
                    />
                ))}

                {AXES.map((axis) => {
                    const end = polarToCartesian(cx, cy, maxRadius, axis.angleDeg);
                    return (
                        <line
                            key={axis.key}
                            x1={cx}
                            y1={cy}
                            x2={end.x}
                            y2={end.y}
                            stroke="#D1D5DB"
                            strokeWidth={1}
                        />
                    );
                })}

                <polygon
                    points={polygonPoints}
                    fill="rgba(122, 200, 255, 0.45)"
                    stroke="#0092D7"
                    strokeWidth={2.5}
                    strokeLinejoin="round"
                />

                {dataPoints.map((point) => (
                    <circle
                        key={point.key}
                        cx={point.x}
                        cy={point.y}
                        r={4}
                        fill="#0092D7"
                        stroke="#ffffff"
                        strokeWidth={1.5}
                    />
                ))}
            </svg>

            <div className="pointer-events-none absolute inset-0">
                {AXES.map((axis) => {
                    const labelRadius = maxRadius + size * labelOffsetRatio;
                    const pos = polarToCartesian(cx, cy, labelRadius, axis.angleDeg);
                    const isTop = axis.angleDeg === -90;
                    const isBottom = axis.angleDeg === 90;
                    const isLeft = axis.angleDeg === 180;
                    const isRight = axis.angleDeg === 0;
                    const axisLabelClass =
                        "nowrap" in axis && axis.nowrap
                            ? variant === "narrow"
                                ? "whitespace-nowrap text-[11px] leading-snug"
                                : "whitespace-nowrap text-[12px] leading-snug"
                            : labelClassName;

                    return (
                        <div
                            key={axis.key}
                            className={`absolute text-center text-[#4E4E4E] ${axisLabelClass}`}
                            style={{
                                left: `${(pos.x / size) * 100}%`,
                                top: `${(pos.y / size) * 100}%`,
                                transform: `translate(-50%, -50%)${isTop ? " translateY(-2px)" : ""}${isBottom ? " translateY(2px)" : ""}${isLeft ? " translateX(-3px)" : ""}${isRight ? " translateX(3px)" : ""}`,
                            }}
                        >
                            {axis.label}
                        </div>
                    );
                })}
            </div>
            </div>
        </div>
    );
}
